import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Trophy, Skull, Zap, Star, Timer, Target, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import confetti from 'canvas-confetti';
import SkillBar from './SkillBar';

// Class configuration for type advantages
const CLASS_CONFIG = {
  meme: { emoji: '🐸', color: '#00FF99', name: 'MEME', beats: 'tech' },
  tech: { emoji: '🤖', color: '#00CCFF', name: 'TECH', beats: 'politics' },
  politics: { emoji: '🏛️', color: '#FF6B6B', name: 'POLITICS', beats: 'meme' },
};

// Rarity colors
const RARITY_COLORS = {
  common: '#888888',
  rare: '#00CCFF',
  epic: '#FF00FF',
  legendary: '#FFD700',
};

// Card Back Component
const CardBack = ({ size = 'normal', isRevealing = false }) => (
  <motion.div 
    className={`relative ${size === 'small' ? 'w-20 h-28' : 'w-32 h-44'} rounded-xl overflow-hidden`}
    animate={isRevealing ? { rotateY: 180 } : { rotateY: 0 }}
    transition={{ duration: 0.6 }}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-black to-cyan-900" />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-cyan-500 rotate-45 animate-pulse" />
      <div className="absolute w-8 h-8 border-4 border-pink-500 rotate-45 animate-pulse" style={{ animationDelay: '0.5s' }} />
    </div>
    <div className="absolute inset-2 border-2 border-white/10 rounded-lg" />
    <div className="absolute bottom-2 left-0 right-0 text-center">
      <span className="font-anton text-xs text-white/30 tracking-widest">SLAP</span>
    </div>
  </motion.div>
);

// Battle Card Component
const BattleCard = ({ 
  card, 
  onClick, 
  isSelectable = false, 
  isSelected = false,
  isPlayed = false,
  isWinner = false,
  isLoser = false,
  showTypeAdvantage = false,
  size = 'normal'
}) => {
  const cardClass = CLASS_CONFIG[card?.card_class] || CLASS_CONFIG.meme;
  const borderColor = RARITY_COLORS[card?.rarity] || '#00CCFF';
  const sizeClasses = size === 'small' ? 'w-20 h-28' : size === 'large' ? 'w-40 h-56' : 'w-32 h-44';
  
  return (
    <motion.div
      className={`relative ${sizeClasses} rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
        isSelectable ? 'hover:scale-105 hover:shadow-[0_0_30px_rgba(0,255,255,0.6)]' : ''
      } ${isSelected ? 'ring-4 ring-neon-green scale-105' : ''} ${
        isWinner ? 'ring-4 ring-yellow-400 shadow-[0_0_40px_rgba(255,215,0,0.8)]' : ''
      } ${isLoser ? 'ring-4 ring-red-500 shadow-[0_0_40px_rgba(255,0,0,0.6)]' : ''}`}
      style={{ 
        border: `3px solid ${isWinner ? '#FFD700' : isLoser ? '#FF0000' : borderColor}`,
        boxShadow: `0 10px 40px rgba(0,0,0,0.5), 0 0 20px ${borderColor}40`
      }}
      onClick={onClick}
      whileHover={isSelectable ? { y: -10 } : {}}
      whileTap={isSelectable ? { scale: 0.95 } : {}}
      initial={isPlayed ? { y: 100, opacity: 0, rotateY: 180 } : {}}
      animate={isPlayed ? { y: 0, opacity: 1, rotateY: 0 } : {}}
      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
    >
      {/* Card Image */}
      {card?.image_base64 ? (
        <img
          src={`data:image/png;base64,${card.image_base64}`}
          alt={card?.character_name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <span className="font-anton text-2xl text-white/50">?</span>
        </div>
      )}
      
      {/* Class Badge */}
      <div 
        className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold"
        style={{ background: `${cardClass.color}40`, color: cardClass.color }}
      >
        {cardClass.emoji}
      </div>
      
      {/* Card Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-2">
        <p className="font-anton text-xs text-white truncate">{card?.character_name}</p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold" style={{ color: cardClass.color }}>
            {cardClass.name}
          </span>
          <div className="flex items-center gap-1">
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            <span className="font-anton text-white text-xs">{card?.power_rating || card?.final_power}</span>
          </div>
        </div>
      </div>
      
      {/* Winner/Loser Badge */}
      {isWinner && (
        <motion.div
          className="absolute top-2 left-2 bg-yellow-500 rounded-full p-1"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: 'spring' }}
        >
          <Trophy className="w-4 h-4 text-black" />
        </motion.div>
      )}
      {isLoser && (
        <motion.div
          className="absolute top-2 left-2 bg-red-600 rounded-full p-1"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: 'spring' }}
        >
          <Skull className="w-4 h-4 text-white" />
        </motion.div>
      )}
      
      {/* Type Advantage Indicator */}
      {showTypeAdvantage && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="text-center">
            <Zap className="w-8 h-8 text-yellow-400 mx-auto animate-pulse" />
            <span className="font-anton text-yellow-400 text-sm">+2 BONUS</span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Impact Text Component
const ImpactText = ({ text, color, size = 'text-6xl' }) => (
  <motion.div
    className={`${size} font-anton uppercase tracking-wider absolute z-50`}
    style={{
      color,
      textShadow: `
        0 0 10px ${color},
        0 0 20px ${color},
        0 0 40px ${color},
        3px 3px 0 #000,
        -3px -3px 0 #000
      `,
      WebkitTextStroke: '2px black',
    }}
    initial={{ scale: 0, rotate: -15 }}
    animate={{ scale: [0, 1.4, 1], rotate: [-15, 10, 0] }}
    exit={{ scale: 0, opacity: 0 }}
    transition={{ duration: 0.4, ease: 'backOut' }}
  >
    {text}
  </motion.div>
);

// Timer Component
const CountdownTimer = ({ seconds, onTimeout, isPaused = false }) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const timerRef = useRef(null);
  
  useEffect(() => {
    setTimeLeft(seconds);
  }, [seconds]);
  
  useEffect(() => {
    if (isPaused || timeLeft <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      if (timeLeft <= 0 && onTimeout) onTimeout();
      return;
    }
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeLeft, onTimeout, isPaused]);
  
  const percentage = (timeLeft / seconds) * 100;
  const isUrgent = timeLeft <= 3;
  
  return (
    <motion.div 
      className={`flex items-center gap-3 px-4 py-2 rounded-full ${
        isUrgent ? 'bg-red-900/60 border-2 border-red-500' : 'bg-black/60 border-2 border-white/20'
      }`}
      animate={isUrgent ? { scale: [1, 1.05, 1] } : {}}
      transition={{ duration: 0.5, repeat: isUrgent ? Infinity : 0 }}
    >
      <Timer className={`w-5 h-5 ${isUrgent ? 'text-red-500 animate-pulse' : 'text-white/60'}`} />
      <div className="w-24 h-2 bg-white/20 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${isUrgent ? 'bg-red-500' : 'bg-neon-green'}`}
          initial={{ width: '100%' }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
      <span className={`font-anton text-xl ${isUrgent ? 'text-red-500' : 'text-white'}`}>
        {timeLeft}s
      </span>
    </motion.div>
  );
};

// Main Interactive Battle Component
const InteractiveBattle = ({ 
  playerCards, 
  opponentCards,
  onBattleComplete,
  sessionId
}) => {
  // Battle state
  const [phase, setPhase] = useState('intro'); // intro, waiting, skillbar, reveal, clash, result, victory, defeat
  const [currentRound, setCurrentRound] = useState(0);
  const [playerScore, setPlayerScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  
  // Card state
  const [playerHand, setPlayerHand] = useState([...playerCards]);
  const [opponentHand, setOpponentHand] = useState([...opponentCards]);
  const [selectedPlayerCard, setSelectedPlayerCard] = useState(null);
  const [revealedOpponentCard, setRevealedOpponentCard] = useState(null);
  const [playedCards, setPlayedCards] = useState({ player: [], opponent: [] });
  
  // Skill bar state
  const [skillBarResult, setSkillBarResult] = useState(null);
  const [sentimentModifier, setSentimentModifier] = useState(0);
  
  // Round results
  const [roundWinner, setRoundWinner] = useState(null);
  const [showImpact, setShowImpact] = useState(null);
  const [hasTypeAdvantage, setHasTypeAdvantage] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [damageFlash, setDamageFlash] = useState(null); // 'player' or 'opponent'
  const [showSlapEffect, setShowSlapEffect] = useState(null); // 'player' or 'opponent' - SLAP hit visual
  
  // Forfeit warning state
  const [showForfeitWarning, setShowForfeitWarning] = useState(false);
  
  // Timer state
  const [timerActive, setTimerActive] = useState(false);
  const TURN_TIME = 10;
  
  // Handle back/exit attempt
  const handleExitAttempt = useCallback(() => {
    if (phase !== 'result') {
      setShowForfeitWarning(true);
      return true; // Prevent default
    }
    return false;
  }, [phase]);
  
  // Add event listener for browser back/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (phase !== 'result') {
        e.preventDefault();
        e.returnValue = 'You will lose your stake if you leave!';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [phase]);
  
  // SLAP hit effect - particles and screen impact
  const triggerSlapEffect = useCallback((target) => {
    setShowSlapEffect(target);
    
    // Particle burst
    confetti({
      particleCount: 50,
      spread: 90,
      origin: { x: target === 'opponent' ? 0.75 : 0.25, y: 0.4 },
      colors: target === 'opponent' ? ['#FF0000', '#FF6600', '#FFFF00'] : ['#00FF00', '#00FFFF', '#FFFFFF'],
      startVelocity: 25,
      gravity: 0.8
    });
    
    setTimeout(() => setShowSlapEffect(null), 600);
  }, []);
  
  // Trigger screen shake and flash on damage
  const triggerDamageEffect = useCallback((target) => {
    setScreenShake(true);
    setDamageFlash(target);
    triggerSlapEffect(target === 'player' ? 'player' : 'opponent');
    setTimeout(() => {
      setScreenShake(false);
      setDamageFlash(null);
    }, 500);
  }, [triggerSlapEffect]);
  
  // Confetti on win
  const triggerWinConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;
    
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#FFD700', '#FF00FF', '#00FFFF']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#FFD700', '#FF00FF', '#00FFFF']
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, []);
  
  // Check type advantage
  const checkTypeAdvantage = useCallback((playerClass, opponentClass) => {
    const config = CLASS_CONFIG[playerClass];
    return config && config.beats === opponentClass;
  }, []);
  
  // Calculate round winner
  const calculateRoundWinner = useCallback((playerCard, opponentCard, skillBonus = 0) => {
    const playerClass = playerCard.card_class || 'meme';
    const opponentClass = opponentCard.card_class || 'meme';
    
    let playerPower = playerCard.power_rating || 3;
    let opponentPower = opponentCard.power_rating || 3;
    
    // Add skill bar bonus to player power
    playerPower += skillBonus;
    
    // Type advantage bonus
    const playerHasAdvantage = checkTypeAdvantage(playerClass, opponentClass);
    const opponentHasAdvantage = checkTypeAdvantage(opponentClass, playerClass);
    
    if (playerHasAdvantage) playerPower += 2;
    if (opponentHasAdvantage) opponentPower += 2;
    
    return {
      winner: playerPower >= opponentPower ? 'player' : 'opponent',
      playerPower,
      opponentPower,
      playerHasAdvantage,
      opponentHasAdvantage,
      skillBonus
    };
  }, [checkTypeAdvantage]);
  
  // Start round (show waiting state)
  const startRound = useCallback(() => {
    setPhase('waiting');
    setSelectedPlayerCard(null);
    setRevealedOpponentCard(null);
    setRoundWinner(null);
    setShowImpact(null);
    setHasTypeAdvantage(false);
    setSkillBarResult(null);
    setTimerActive(true);
    
    // Randomize sentiment modifier for each round (-1 to +1)
    // This simulates news affecting the battle
    setSentimentModifier(Math.floor(Math.random() * 3) - 1);
  }, []);
  
  // Handle card selection - now triggers skill bar
  const handleCardSelect = useCallback((card) => {
    if (phase !== 'waiting') return;
    
    setSelectedPlayerCard(card);
    setTimerActive(false);
    
    // Remove card from hand
    setPlayerHand(prev => prev.filter(c => c.id !== card.id));
    
    // Move to skill bar phase instead of reveal
    setPhase('skillbar');
  }, [phase]);
  
  // Handle skill bar completion
  const handleSkillBarComplete = useCallback((skillResult) => {
    setSkillBarResult(skillResult);
    
    // Move to reveal phase
    setPhase('reveal');
    
    // After a short delay, reveal opponent's card
    setTimeout(() => {
      const opponentCard = opponentHand[currentRound];
      setRevealedOpponentCard(opponentCard);
      
      // Calculate winner with skill bonus
      const skillBonus = skillResult.timing.bonus;
      const result = calculateRoundWinner(selectedPlayerCard, opponentCard, skillBonus);
      setHasTypeAdvantage(result.playerHasAdvantage);
      
      // Show clash phase
      setTimeout(() => {
        setPhase('clash');
        setRoundWinner(result.winner);
        
        // Show impact text based on skill bar result
        if (skillResult.timing.text === 'PERFECT!' && result.winner === 'player') {
          setShowImpact({ text: 'PERFECT STRIKE!', color: '#FFD700' });
        } else if (result.playerHasAdvantage) {
          setShowImpact({ text: 'CRITICAL HIT!', color: '#FFD700' });
        } else if (result.winner === 'player') {
          setShowImpact({ text: 'POW!', color: '#00FF99' });
        } else if (skillResult.timing.text === 'MISS!') {
          setShowImpact({ text: 'MISSED!', color: '#FF0066' });
        } else {
          setShowImpact({ text: 'CRASH!', color: '#FF0066' });
        }
        
        // Update scores with damage effects
        if (result.winner === 'player') {
          setPlayerScore(prev => prev + 1);
          triggerDamageEffect('opponent');
        } else {
          setOpponentScore(prev => prev + 1);
          triggerDamageEffect('player');
        }
        
        // Add to played cards
        setPlayedCards(prev => ({
          player: [...prev.player, selectedPlayerCard],
          opponent: [...prev.opponent, opponentCard]
        }));
        
        // Show result phase after impact
        setTimeout(() => {
          setShowImpact(null);
          setPhase('result');
        }, 1500);
        
      }, 1000);
    }, 800);
  }, [currentRound, opponentHand, calculateRoundWinner, selectedPlayerCard]);
  
  // Handle timeout - auto-select first available card
  const handleTimeout = useCallback(() => {
    if (phase === 'waiting' && playerHand.length > 0) {
      handleCardSelect(playerHand[0]);
    }
  }, [phase, playerHand, handleCardSelect]);
  
  // Proceed to next round
  const handleNextRound = useCallback(() => {
    if (currentRound < 2) {
      setCurrentRound(prev => prev + 1);
      startRound();
    } else {
      // Battle complete
      const finalWinner = playerScore >= opponentScore ? 'player' : 'opponent';
      setPhase(finalWinner === 'player' ? 'victory' : 'defeat');
      if (finalWinner === 'player') {
        triggerWinConfetti();
      }
    }
  }, [currentRound, playerScore, opponentScore, startRound, triggerWinConfetti]);
  
  // Initialize battle
  useEffect(() => {
    const introTimer = setTimeout(() => {
      startRound();
    }, 2000);
    
    return () => clearTimeout(introTimer);
  }, [startRound]);
  
  // Handle battle complete
  const handleBattleEnd = useCallback(() => {
    const result = {
      winner: playerScore > opponentScore ? 'player1' : 'player2',
      player1_rounds_won: playerScore,
      player2_rounds_won: opponentScore,
      rounds: playedCards.player.map((card, i) => ({
        round: i + 1,
        player1: {
          card_name: card.character_name,
          card_class: card.card_class,
          power_rating: card.power_rating
        },
        player2: {
          card_name: playedCards.opponent[i]?.character_name,
          card_class: playedCards.opponent[i]?.card_class,
          power_rating: playedCards.opponent[i]?.power_rating
        }
      }))
    };
    
    if (onBattleComplete) {
      onBattleComplete(result, playerScore > opponentScore);
    }
  }, [playerScore, opponentScore, playedCards, onBattleComplete]);
  
  return (
    <motion.div
      className="fixed inset-0 z-[1000] overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: 1,
        x: screenShake ? [0, -10, 10, -10, 10, 0] : 0
      }}
      transition={{ duration: screenShake ? 0.3 : 0.2 }}
      exit={{ opacity: 0 }}
    >
      {/* DAMAGE FLASH OVERLAY */}
      <AnimatePresence>
        {damageFlash === 'player' && (
          <motion.div
            className="absolute inset-0 z-[999] pointer-events-none"
            style={{ 
              background: 'radial-gradient(circle at center, rgba(255,0,0,0.4) 0%, transparent 70%)',
              boxShadow: 'inset 0 0 100px rgba(255,0,0,0.5)'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          />
        )}
        {damageFlash === 'opponent' && (
          <motion.div
            className="absolute inset-0 z-[999] pointer-events-none"
            style={{ 
              background: 'radial-gradient(circle at center, rgba(0,255,0,0.3) 0%, transparent 70%)',
              boxShadow: 'inset 0 0 100px rgba(0,255,0,0.4)'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          />
        )}
        
        {/* SLAP HIT EFFECT - Visual impact indicator */}
        {showSlapEffect && (
          <motion.div
            className="absolute z-[1000] pointer-events-none"
            style={{
              left: showSlapEffect === 'opponent' ? '60%' : '20%',
              top: '30%',
            }}
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: [0, 3, 4], opacity: [1, 1, 0] }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-8xl">💥</div>
            <motion.p
              className={`font-anton text-6xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${
                showSlapEffect === 'opponent' ? 'text-red-500' : 'text-green-500'
              }`}
              initial={{ scale: 0, rotate: -15 }}
              animate={{ scale: [0, 1.5, 1], rotate: [15, -5, 0] }}
            >
              SLAP!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* FORFEIT WARNING MODAL */}
      <AnimatePresence>
        {showForfeitWarning && (
          <motion.div
            className="fixed inset-0 z-[2000] bg-black/90 backdrop-blur-sm flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-gradient-to-b from-red-900/80 to-black border-4 border-red-500 rounded-2xl p-8 max-w-md mx-4 text-center"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
            >
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4 animate-pulse" />
              <h2 className="font-anton text-3xl text-red-400 mb-4">⚠️ FORFEIT MATCH?</h2>
              <p className="text-white/80 mb-6">
                If you leave now, you <span className="text-red-400 font-bold">LOSE YOUR STAKE</span>!
              </p>
              <p className="text-yellow-400 text-sm mb-6">
                Entry fee will NOT be refunded.
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => setShowForfeitWarning(false)}
                  className="flex-1 py-4 bg-green-600 hover:bg-green-500 text-white font-bold text-lg"
                >
                  STAY & FIGHT
                </Button>
                <Button
                  onClick={() => {
                    setShowForfeitWarning(false);
                    onBattleComplete?.({ forfeited: true }, false);
                  }}
                  className="flex-1 py-4 bg-red-600 hover:bg-red-500 text-white font-bold text-lg"
                >
                  SURRENDER
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Arena Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000020 0%, #100030 50%, #200040 100%)',
          }}
        />
        
        {/* Grid Floor */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-1/2"
          style={{
            background: `
              linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.8) 100%),
              repeating-linear-gradient(90deg, transparent, transparent 80px, rgba(0,255,255,0.1) 80px, rgba(0,255,255,0.1) 82px),
              repeating-linear-gradient(0deg, transparent, transparent 80px, rgba(255,0,255,0.1) 80px, rgba(255,0,255,0.1) 82px)
            `,
            transform: 'perspective(500px) rotateX(60deg)',
            transformOrigin: 'bottom',
          }}
        />
        
        {/* Arena lights */}
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-pink-500/20 rounded-full blur-[100px]" />
        <div className="absolute top-0 right-1/4 w-32 h-32 bg-cyan-500/20 rounded-full blur-[100px]" />
        
        {/* Neon border */}
        <div className="absolute inset-4 border-[3px] border-cyan-500/30 rounded-lg pointer-events-none" 
          style={{ boxShadow: 'inset 0 0 100px rgba(0,255,255,0.1)' }} 
        />
      </div>
      
      {/* HIGH PRESSURE HUD - Score Board */}
      <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-20">
        <motion.div 
          className="flex items-center gap-6 bg-black/90 border-4 border-yellow-500/50 rounded-2xl px-10 py-6"
          style={{ boxShadow: '0 0 40px rgba(255,215,0,0.3), inset 0 0 60px rgba(0,0,0,0.8)' }}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {/* PLAYER SCORE - GREEN */}
          <motion.div 
            className="text-center relative"
            animate={playerScore > 0 ? { scale: [1, 1.1, 1] } : {}}
          >
            <motion.p 
              className="font-anton text-2xl uppercase tracking-widest mb-2"
              style={{ 
                color: '#00FF99',
                textShadow: '0 0 10px #00FF99, 0 0 20px #00FF99'
              }}
            >
              ★ YOU ★
            </motion.p>
            <motion.div
              className="relative"
              key={`player-${playerScore}`}
              initial={{ scale: 1 }}
              animate={{ 
                scale: [1, 1.4, 1],
                textShadow: [
                  '0 0 20px #00FF99',
                  '0 0 60px #00FF99, 0 0 100px #00FF99',
                  '0 0 20px #00FF99'
                ]
              }}
              transition={{ duration: 0.4 }}
            >
              <span 
                className="font-anton text-8xl"
                style={{ 
                  color: '#00FF99',
                  textShadow: '0 0 30px #00FF99, 0 0 60px #00FF99, 0 4px 0 #006633',
                  WebkitTextStroke: '2px #004422'
                }}
              >
                {playerScore}
              </span>
            </motion.div>
          </motion.div>
          
          {/* VS DIVIDER */}
          <div className="relative px-6">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            >
              <Swords className="w-14 h-14 text-yellow-500" style={{ filter: 'drop-shadow(0 0 10px #FFD700)' }} />
            </motion.div>
            <motion.p 
              className="font-anton text-xl text-yellow-400 absolute -bottom-6 left-1/2 -translate-x-1/2"
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              VS
            </motion.p>
          </div>
          
          {/* OPPONENT SCORE - RED & THREATENING */}
          <motion.div 
            className="text-center relative"
            key={`opponent-shake-${opponentScore}`}
            animate={opponentScore > 0 ? { 
              x: [0, -5, 5, -5, 5, 0],
              scale: [1, 1.1, 1]
            } : {}}
            transition={{ duration: 0.3 }}
          >
            <motion.p 
              className="font-anton text-2xl uppercase tracking-widest mb-2"
              style={{ 
                color: '#FF4444',
                textShadow: '0 0 10px #FF0000, 0 0 20px #FF0000'
              }}
            >
              ENEMY SCORE
            </motion.p>
            <motion.div
              className="relative"
              key={`opponent-${opponentScore}`}
              initial={{ scale: 1 }}
              animate={{ 
                scale: [1, 1.4, 1],
              }}
              transition={{ duration: 0.4 }}
            >
              <span 
                className="font-anton text-8xl"
                style={{ 
                  color: '#FF4444',
                  textShadow: '0 0 30px #FF0000, 0 0 60px #FF0000, 0 4px 0 #660000',
                  WebkitTextStroke: '2px #440000'
                }}
              >
                {opponentScore}
              </span>
            </motion.div>
            
            {/* Damage Flash Effect when opponent scores */}
            <AnimatePresence>
              {opponentScore > 0 && (
                <motion.div
                  className="absolute inset-0 bg-red-500/30 rounded-xl"
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                />
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
        
        {/* Round Indicator - Below Score */}
        <motion.div 
          className="flex justify-center items-center gap-3 mt-4 bg-black/60 rounded-full px-6 py-2 mx-auto w-fit"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <span className="font-space text-sm text-white/60 uppercase">Round</span>
          {[0, 1, 2].map((i) => (
            <motion.div 
              key={i}
              className={`w-6 h-6 rounded-full border-3 flex items-center justify-center transition-all duration-300 ${
                i < currentRound ? 'bg-green-500 border-green-400' :
                i === currentRound ? 'bg-yellow-500 border-yellow-400 animate-pulse' :
                'bg-transparent border-white/30'
              }`}
              animate={i === currentRound ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.5, repeat: i === currentRound ? Infinity : 0 }}
            >
              {i < currentRound && <span className="text-black font-bold text-xs">✓</span>}
              {i === currentRound && <span className="text-black font-bold text-xs">{i + 1}</span>}
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      {/* Main Battle Area */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        
        {/* INTRO PHASE */}
        <AnimatePresence>
          {phase === 'intro' && (
            <motion.div
              className="text-center z-30"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0, opacity: 0 }}
            >
              <motion.h1 
                className="font-anton text-6xl md:text-8xl text-white"
                animate={{ 
                  textShadow: [
                    '0 0 20px #FF00FF, 0 0 40px #FF00FF',
                    '0 0 40px #00FFFF, 0 0 80px #00FFFF',
                    '0 0 20px #FF00FF, 0 0 40px #FF00FF'
                  ]
                }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                GET READY!
              </motion.h1>
              <p className="font-space text-xl text-white/60 mt-4">Best of 3 Rounds - Precision Timing System</p>
              <p className="font-space text-sm text-yellow-400 mt-2">Select card + Hit the timing bar for bonus damage!</p>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* WAITING PHASE - User must select card */}
        <AnimatePresence>
          {phase === 'waiting' && (
            <motion.div
              className="flex flex-col items-center gap-8 z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Timer */}
              <CountdownTimer 
                seconds={TURN_TIME} 
                onTimeout={handleTimeout}
                isPaused={!timerActive}
              />
              
              {/* Instruction */}
              <motion.div
                className="text-center"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <h2 className="font-anton text-3xl text-neon-green mb-2">
                  CHOOSE YOUR FIGHTER FOR ROUND {currentRound + 1}!
                </h2>
                <p className="font-space text-white/60">Click a card from your hand below</p>
              </motion.div>
              
              {/* Opponent's Hidden Cards */}
              <div className="flex gap-4 mb-8 relative">
                <motion.p 
                  className="font-anton text-lg text-red-500 absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap tracking-wider"
                  animate={{ 
                    scale: [1, 1.05, 1],
                    textShadow: ['0 0 10px #FF0000', '0 0 20px #FF0000', '0 0 10px #FF0000']
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  OPPONENT
                </motion.p>
                {opponentHand.slice(currentRound).map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <CardBack size="normal" />
                  </motion.div>
                ))}
              </div>
              
              {/* Battle Slot */}
              <div className="flex items-center gap-16 my-8">
                <div className="w-40 h-56 border-4 border-dashed border-white/30 rounded-xl flex items-center justify-center">
                  <span className="font-space text-white/30 text-sm">YOUR CARD</span>
                </div>
                <Swords className="w-12 h-12 text-yellow-500 animate-pulse" />
                <div className="w-40 h-56 border-4 border-dashed border-white/30 rounded-xl flex items-center justify-center">
                  <span className="font-space text-red-400/50 text-sm">ENEMY CARD</span>
                </div>
              </div>
              
              {/* Player's Hand */}
              <div className="relative">
                <p className="font-space text-xs text-white/40 absolute -top-6 left-1/2 transform -translate-x-1/2">YOUR HAND - CLICK TO SELECT</p>
                <div className="flex gap-6">
                  {playerHand.map((card, i) => (
                    <motion.div
                      key={card.id}
                      initial={{ y: 100, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <BattleCard
                        card={card}
                        isSelectable={true}
                        onClick={() => handleCardSelect(card)}
                        size="large"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* SKILL BAR PHASE - Precision Timing */}
        <AnimatePresence>
          {phase === 'skillbar' && selectedPlayerCard && (
            <motion.div
              className="flex flex-col items-center gap-8 z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Header */}
              <motion.div
                className="text-center"
                initial={{ y: -50 }}
                animate={{ y: 0 }}
              >
                <h2 className="font-anton text-3xl text-yellow-400 mb-2">
                  PRECISION ATTACK!
                </h2>
                <p className="font-space text-white/60">Tap in the GREEN zone for bonus damage!</p>
              </motion.div>
              
              {/* Selected Card Preview */}
              <div className="flex items-center gap-8">
                <BattleCard
                  card={selectedPlayerCard}
                  size="normal"
                />
                
                <div className="text-center">
                  <Zap className="w-12 h-12 text-yellow-400 mx-auto mb-2 animate-pulse" />
                  <p className="font-anton text-xl text-white">VS</p>
                  <p className="font-space text-xs text-red-400">☠️ ENEMY</p>
                </div>
                
                <CardBack size="normal" />
              </div>
              
              {/* The Skill Bar */}
              <div className="w-full max-w-lg">
                <SkillBar
                  onComplete={handleSkillBarComplete}
                  sentimentModifier={sentimentModifier}
                  cardPower={selectedPlayerCard?.power_rating || 3}
                  cardClass={selectedPlayerCard?.card_class || 'meme'}
                />
              </div>
              
              {/* Sentiment Info */}
              {sentimentModifier !== 0 && (
                <motion.div
                  className={`px-4 py-2 rounded-lg text-sm ${
                    sentimentModifier > 0 
                      ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                      : 'bg-red-500/20 border border-red-500/50 text-red-400'
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <span className="font-space">
                    {sentimentModifier > 0 
                      ? '📈 Positive News: Larger green zone!' 
                      : '📉 Negative News: Smaller green zone!'}
                  </span>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* REVEAL & CLASH PHASE */}
        <AnimatePresence>
          {(phase === 'reveal' || phase === 'clash' || phase === 'result') && (
            <motion.div
              className="flex flex-col items-center gap-8 z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {/* Round indicator */}
              <div className="text-center mb-4">
                <p className="font-anton text-2xl text-yellow-400">ROUND {currentRound + 1}</p>
              </div>
              
              {/* Battle Cards */}
              <div className="flex items-center gap-16">
                {/* Player's Selected Card */}
                <motion.div
                  initial={{ x: -200, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >
                  <BattleCard
                    card={selectedPlayerCard}
                    isPlayed={true}
                    isWinner={roundWinner === 'player' && phase === 'clash'}
                    isLoser={roundWinner === 'opponent' && phase === 'clash'}
                    showTypeAdvantage={hasTypeAdvantage && phase === 'clash'}
                    size="large"
                  />
                </motion.div>
                
                {/* VS / Impact */}
                <div className="relative w-32 h-32 flex items-center justify-center">
                  {showImpact ? (
                    <ImpactText text={showImpact.text} color={showImpact.color} />
                  ) : (
                    <Swords className="w-16 h-16 text-yellow-500 animate-pulse" />
                  )}
                </div>
                
                {/* Opponent's Card */}
                <motion.div
                  initial={{ x: 200, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.5 }}
                >
                  {revealedOpponentCard ? (
                    <BattleCard
                      card={revealedOpponentCard}
                      isPlayed={true}
                      isWinner={roundWinner === 'opponent' && phase === 'clash'}
                      isLoser={roundWinner === 'player' && phase === 'clash'}
                      size="large"
                    />
                  ) : (
                    <CardBack size="large" isRevealing={true} />
                  )}
                </motion.div>
              </div>
              
              {/* Next Round Button */}
              {phase === 'result' && (
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    onClick={handleNextRound}
                    className="bg-neon-green hover:bg-neon-green/80 text-black font-anton text-xl px-10 py-4"
                    data-testid="next-round-btn"
                  >
                    {currentRound < 2 ? `NEXT ROUND (${currentRound + 2}/3)` : 'SEE RESULTS'}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* VICTORY PHASE */}
        <AnimatePresence>
          {phase === 'victory' && (
            <motion.div
              className="text-center z-30"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              <motion.div
                animate={{ y: [0, -10, 0], rotate: [0, -2, 2, 0] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                <Trophy className="w-32 h-32 mx-auto text-yellow-500 mb-4" />
              </motion.div>
              <motion.h1 
                className="font-anton text-6xl md:text-8xl"
                style={{
                  background: 'linear-gradient(180deg, #FFD700 0%, #FF8C00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                VICTORY!
              </motion.h1>
              <p className="font-space text-xl text-neon-green mt-4">+25 Shards Earned!</p>
              <p className="font-space text-white/60 mt-2">Final Score: {playerScore} - {opponentScore}</p>
              <Button
                onClick={handleBattleEnd}
                className="mt-8 bg-yellow-500 hover:bg-yellow-400 text-black font-anton text-xl px-8 py-4"
                data-testid="collect-rewards-btn"
              >
                COLLECT REWARDS
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* DEFEAT PHASE */}
        <AnimatePresence>
          {phase === 'defeat' && (
            <motion.div
              className="text-center z-30"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              <motion.div
                animate={{ x: [0, -3, 3, -2, 2, 0] }}
                transition={{ duration: 0.1, repeat: 5 }}
              >
                <Skull className="w-32 h-32 mx-auto text-red-500 mb-4" />
              </motion.div>
              <h1 className="font-anton text-6xl md:text-8xl text-red-500">YOU LOST</h1>
              <p className="font-space text-xl text-white/60 mt-4">Better luck next time...</p>
              <p className="font-space text-white/60 mt-2">Final Score: {playerScore} - {opponentScore}</p>
              <Button
                onClick={handleBattleEnd}
                className="mt-8 bg-red-600 hover:bg-red-500 text-white font-anton text-xl px-8 py-4"
                data-testid="try-again-btn"
              >
                TRY AGAIN
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Skip Button - Now triggers forfeit warning */}
      <Button
        onClick={() => {
          if (phase !== 'victory' && phase !== 'defeat') {
            setShowForfeitWarning(true);
          } else {
            handleBattleEnd();
          }
        }}
        className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 text-white border border-white/20"
        data-testid="exit-battle-btn"
      >
        Exit Battle
      </Button>
    </motion.div>
  );
};

export default InteractiveBattle;
